<template>
  <!-- The ticket mark (spec §6): a ticket type in its wristband colour, a custom colour, or none (a soft fill in the text colour, only when another ticket of the activity has a colour; otherwise, or with colours switched off, no mark at all), or the same card drawn hollow with a plus for an extra (in the text colour: ink, white in dark mode or on a dark pill).
       The viewBox is padded by one pixel all round (taken back with -m-px, so the mark lays out at its nominal size)
       to make room for the white contour: a sticker edge that shows on dark grounds and vanishes on white. -->
  <!-- A ticket without a colour in an activity where none has one: no mark at all, just the name (see useTicketColors().inUse) -->
  <svg v-if="kind === 'extra' || fill || inUse || neutral" :width="size + 2" :height="height + 2" :viewBox="viewBox" class="-m-px shrink-0 inline-block align-middle"
    :role="label ? 'img' : undefined" :aria-label="label || undefined" :aria-hidden="label ? undefined : 'true'">
    <template v-if="kind === 'ticket'">
      <polygon :points="TICKET_MARK.points" :fill="fill || 'currentColor'" :fill-opacity="fill ? undefined : 0.4"
        stroke="#fff" stroke-width="2" stroke-linejoin="round" paint-order="stroke" vector-effect="non-scaling-stroke" />
      <!-- A light colour (a pale custom one, the yellow wristband) gets a hairline, or it melts into white -->
      <polygon v-if="light" :points="TICKET_MARK.hollow" fill="none" stroke="rgba(19,19,19,.28)" stroke-width="1"
        stroke-linejoin="round" vector-effect="non-scaling-stroke" />
    </template>
    <g v-else fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
      <polygon :points="TICKET_MARK.hollow" stroke-linejoin="round" vector-effect="non-scaling-stroke" />
      <path :d="plus" vector-effect="non-scaling-stroke" />
    </g>
  </svg>
  <!-- Where the mark stood in for the name (a row of marks with labels), the name takes its place -->
  <span v-else-if="label">{{ label }}</span>
</template>

<script lang="ts" setup>
import { TICKET_MARK } from '~/utils/ticketMark';

const props = withDefaults(defineProps<{
  color?: string;
  kind?: 'ticket' | 'extra';
  size?: 16 | 24 | 40;
  label?: string;
  /** Draw the grey mark even where the activity has no colours (the parts page) */
  neutral?: boolean;
}>(), { kind: 'ticket', size: 16 });

const { width: W, height: H } = TICKET_MARK;
// The organization can switch colours off ("Kleur per ticketsoort"): then every mark is the soft fill, wherever its colour came from
const { enabled, inUse } = useTicketColors();
const fill = computed(() => (enabled.value ? props.color : undefined));
const height = computed(() => (props.size * H) / W);
// One screen pixel in grid units, so the padding is exactly the 1 px the contour needs
const viewBox = computed(() => {
  const p = W / props.size;
  return `${-p} ${-p} ${W + 2 * p} ${H + 2 * p}`;
});
// Light enough to vanish on white (relative luminance over 0.75): a pale custom colour or the yellow wristband
const light = computed(() => {
  const m = /^#?([0-9a-f]{6})$/i.exec(fill.value ?? '');
  if (!m) return false;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(m[1]!.slice(i, i + 2), 16) / 255).map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b! > 0.75;
});
// The extra's plus, in the middle of the card below the cut
const plus = `M${W / 2 - 2.5} ${H / 2 + 0.5}H${W / 2 + 2.5}M${W / 2} ${H / 2 - 2}V${H / 2 + 3}`;
</script>
